import { GAME_SOUND_MUTED_KEY } from '@skillarea/game-layout';

import cashRegisterUrl from './sounds/cash-register.m4a?url';
import fallingCoinsUrl from './sounds/falling-coins.m4a?url';

function isMuted(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage?.getItem(GAME_SOUND_MUTED_KEY) === 'true';
  } catch {
    return false;
  }
}

type TWaveType = 'sine' | 'square' | 'sawtooth' | 'triangle';

const SOUND = {
  envelope: {
    attackSec: 0.01,
    releaseFloor: 0.001,
    startFloor: 0.0001,
  },
  master: {
    gain: 0.9,
  },
  compressor: {
    thresholdDb: -24,
    kneeDb: 24,
    ratio: 8,
    attackSec: 0.003,
    releaseSec: 0.25,
  },
  throttleMs: {
    move: 45,
    hit: 60,
  },
} as const;

type TAudioGraph = {
  ctx: AudioContext;
  out: globalThis.AudioNode;
};

let cachedGraph: TAudioGraph | null = null;

const lastPlayedAtMs: Partial<Record<'move' | 'hit', number>> = {};

const cachedBuffers = new Map<string, globalThis.AudioBuffer>();
const bufferLoadPromises = new Map<
  string,
  Promise<globalThis.AudioBuffer | null>
>();

function shouldPlay(key: 'move' | 'hit', minIntervalMs: number): boolean {
  const now = Date.now();
  const last = lastPlayedAtMs[key] ?? 0;
  if (now - last < minIntervalMs) return false;
  lastPlayedAtMs[key] = now;
  return true;
}

function decodeAudioDataAsync(
  ctx: AudioContext,
  arrayBuffer: ArrayBuffer,
): Promise<globalThis.AudioBuffer> {
  // Safari still has edge-cases around promise-based decodeAudioData.
  return new Promise((resolve, reject) => {
    ctx.decodeAudioData(
      arrayBuffer,
      (buffer) => resolve(buffer),
      (err) => reject(err),
    );
  });
}

async function loadAudioBuffer(
  graph: TAudioGraph,
  url: string,
): Promise<globalThis.AudioBuffer | null> {
  const cached = cachedBuffers.get(url);
  if (cached) return cached;

  const inFlight = bufferLoadPromises.get(url);
  if (inFlight) return inFlight;

  const promise = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const arrayBuffer = await res.arrayBuffer();
      const decoded = await decodeAudioDataAsync(graph.ctx, arrayBuffer);
      cachedBuffers.set(url, decoded);
      return decoded;
    } catch {
      return null;
    } finally {
      bufferLoadPromises.delete(url);
    }
  })();

  bufferLoadPromises.set(url, promise);
  return promise;
}

async function playSample(url: string, { gain = 0.25 } = {}): Promise<void> {
  if (isMuted()) return;
  const graph = getAudioGraph();
  if (!graph) return;

  const buffer = await loadAudioBuffer(graph, url);
  if (!buffer) return;

  try {
    const { ctx } = graph;
    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(gain, ctx.currentTime);

    source.connect(gainNode);
    gainNode.connect(graph.out);

    source.start();
  } catch {
    // ignore
  }
}

function getAudioGraph(): TAudioGraph | null {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;

    if (!cachedGraph || cachedGraph.ctx.state === 'closed') {
      const ctx = new Ctx();

      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(SOUND.master.gain, ctx.currentTime);

      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(
        SOUND.compressor.thresholdDb,
        ctx.currentTime,
      );
      compressor.knee.setValueAtTime(SOUND.compressor.kneeDb, ctx.currentTime);
      compressor.ratio.setValueAtTime(SOUND.compressor.ratio, ctx.currentTime);
      compressor.attack.setValueAtTime(
        SOUND.compressor.attackSec,
        ctx.currentTime,
      );
      compressor.release.setValueAtTime(
        SOUND.compressor.releaseSec,
        ctx.currentTime,
      );

      masterGain.connect(compressor);
      compressor.connect(ctx.destination);

      cachedGraph = { ctx, out: masterGain };
    }

    if (cachedGraph.ctx.state === 'suspended') {
      cachedGraph.ctx.resume().catch(() => {});
    }

    return cachedGraph;
  } catch {
    return null;
  }
}

function playTone(
  graph: TAudioGraph,
  frequency: number,
  startTime: number,
  duration: number,
  gain = 0.08,
  type: TWaveType = 'sine',
): void {
  const { ctx } = graph;
  const osc = ctx.createOscillator();
  const gainNode = ctx.createGain();
  osc.connect(gainNode);
  gainNode.connect(graph.out);
  osc.frequency.value = frequency;
  osc.type = type;

  const safeAttack = Math.min(
    SOUND.envelope.attackSec,
    Math.max(0.001, duration * 0.3),
  );
  gainNode.gain.setValueAtTime(SOUND.envelope.startFloor, startTime);
  gainNode.gain.exponentialRampToValueAtTime(gain, startTime + safeAttack);
  gainNode.gain.exponentialRampToValueAtTime(
    SOUND.envelope.releaseFloor,
    startTime + duration,
  );
  osc.start(startTime);
  osc.stop(startTime + duration);
}

function playGlide(
  graph: TAudioGraph,
  fromFrequency: number,
  toFrequency: number,
  startTime: number,
  duration: number,
  gain = 0.09,
  type: TWaveType = 'sawtooth',
): void {
  const { ctx } = graph;
  const osc = ctx.createOscillator();
  const gainNode = ctx.createGain();
  osc.connect(gainNode);
  gainNode.connect(graph.out);

  osc.type = type;
  osc.frequency.setValueAtTime(fromFrequency, startTime);
  osc.frequency.exponentialRampToValueAtTime(
    Math.max(1, toFrequency),
    startTime + duration,
  );

  const safeAttack = Math.min(
    SOUND.envelope.attackSec,
    Math.max(0.001, duration * 0.3),
  );
  gainNode.gain.setValueAtTime(SOUND.envelope.startFloor, startTime);
  gainNode.gain.exponentialRampToValueAtTime(gain, startTime + safeAttack);
  gainNode.gain.exponentialRampToValueAtTime(
    SOUND.envelope.releaseFloor,
    startTime + duration,
  );

  osc.start(startTime);
  osc.stop(startTime + duration);
}

function playTromboneSlide(
  graph: TAudioGraph,
  fromFrequency: number,
  toFrequency: number,
  startTime: number,
  duration: number,
  gain = 0.12,
): void {
  const { ctx } = graph;
  const osc = ctx.createOscillator();
  const lfo = ctx.createOscillator();
  const lfoGain = ctx.createGain();
  const gainNode = ctx.createGain();

  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(fromFrequency, startTime);
  osc.frequency.exponentialRampToValueAtTime(
    Math.max(1, toFrequency),
    startTime + duration,
  );
  osc.detune.setValueAtTime(-4, startTime);

  lfo.type = 'sine';
  lfo.frequency.setValueAtTime(5.4, startTime);
  lfoGain.gain.setValueAtTime(14, startTime);
  lfo.connect(lfoGain);
  lfoGain.connect(osc.detune);

  osc.connect(gainNode);
  gainNode.connect(graph.out);

  const safeAttack = Math.min(
    SOUND.envelope.attackSec,
    Math.max(0.001, duration * 0.3),
  );
  gainNode.gain.setValueAtTime(SOUND.envelope.startFloor, startTime);
  gainNode.gain.exponentialRampToValueAtTime(gain, startTime + safeAttack);
  gainNode.gain.exponentialRampToValueAtTime(
    SOUND.envelope.releaseFloor,
    startTime + duration,
  );

  osc.start(startTime);
  lfo.start(startTime);
  osc.stop(startTime + duration);
  lfo.stop(startTime + duration);
}

export function playGameStartSound(): void {
  if (isMuted()) return;
  const graph = getAudioGraph();
  if (!graph) return;
  try {
    const t = graph.ctx.currentTime;
    playTone(graph, 523, t, 0.08);
    playTone(graph, 659, t + 0.1, 0.12);
  } catch {
    // ignore
  }
}

export function playOpponentFoundSound(): void {
  if (isMuted()) return;
  const graph = getAudioGraph();
  if (!graph) return;
  try {
    const t = graph.ctx.currentTime;
    playTone(graph, 988, t, 0.06, 0.05, 'square');
    playTone(graph, 1319, t + 0.08, 0.08, 0.06, 'square');
    playTone(graph, 1760, t + 0.18, 0.12, 0.05, 'triangle');
  } catch {
    // ignore
  }
}

export function playMoveSound(): void {
  if (isMuted()) return;
  if (!shouldPlay('move', SOUND.throttleMs.move)) return;
  const graph = getAudioGraph();
  if (!graph) return;
  try {
    const t = graph.ctx.currentTime;
    playTone(graph, 523, t, 0.08);
  } catch {
    // ignore
  }
}

export function playHitSound(): void {
  if (isMuted()) return;
  if (!shouldPlay('hit', SOUND.throttleMs.hit)) return;
  const graph = getAudioGraph();
  if (!graph) return;
  try {
    const t = graph.ctx.currentTime;
    playTone(graph, 660, t, 0.06, 0.1, 'square');
    playTone(graph, 880, t + 0.05, 0.1, 0.08, 'square');
  } catch {
    // ignore
  }
}

export function playShipSunkSound(): void {
  if (isMuted()) return;
  const graph = getAudioGraph();
  if (!graph) return;
  try {
    const t = graph.ctx.currentTime;
    playTone(graph, 440, t, 0.1, 0.1, 'triangle');
    playTone(graph, 330, t + 0.1, 0.15, 0.12, 'triangle');
    playTone(graph, 220, t + 0.22, 0.2, 0.1, 'triangle');
  } catch {
    // ignore
  }
}

export function playWinSound(): void {
  if (isMuted()) return;
  const graph = getAudioGraph();
  if (!graph) return;
  try {
    const t = graph.ctx.currentTime;
    playTone(graph, 523, t, 0.08, 0.06, 'square');
    playTone(graph, 659, t + 0.09, 0.08, 0.06, 'square');
    playTone(graph, 784, t + 0.18, 0.09, 0.06, 'square');
    playTone(graph, 988, t + 0.28, 0.1, 0.06, 'square');

    playTone(graph, 1047, t + 0.42, 0.2, 0.05, 'triangle');
    playTone(graph, 1319, t + 0.42, 0.2, 0.045, 'triangle');
    playTone(graph, 1568, t + 0.42, 0.2, 0.04, 'triangle');
  } catch {
    // ignore
  }
}

export function playLoseSound(): void {
  if (isMuted()) return;
  const graph = getAudioGraph();
  if (!graph) return;
  try {
    const t = graph.ctx.currentTime;
    playTromboneSlide(graph, 392, 330, t, 0.22, 0.12);
    playTromboneSlide(graph, 349, 294, t + 0.26, 0.22, 0.115);
    playTromboneSlide(graph, 311, 247, t + 0.52, 0.32, 0.12);
    playTone(graph, 123, t + 0.86, 0.2, 0.075, 'triangle');
  } catch {
    // ignore
  }
}

export async function playDepositCreditedSound(): Promise<void> {
  await playSample(cashRegisterUrl, { gain: 0.35 });
}

export async function playStakeWinSound(): Promise<void> {
  await playSample(fallingCoinsUrl, { gain: 0.35 });
}

export type TChestOpenSound = 'coins' | 'achievement' | 'empty';

export function playChestOpenSound(result: TChestOpenSound): void {
  if (isMuted()) return;
  const graph = getAudioGraph();
  if (!graph) return;
  try {
    const t = graph.ctx.currentTime;

    playTone(graph, 220, t, 0.08, 0.09, 'triangle');
    playTone(graph, 110, t + 0.06, 0.12, 0.07, 'triangle');

    if (result === 'coins') {
      playTone(graph, 1047, t + 0.08, 0.09, 0.04, 'square');
      playTone(graph, 1319, t + 0.16, 0.1, 0.045, 'square');
      playTone(graph, 1568, t + 0.26, 0.11, 0.05, 'square');
      playTone(graph, 2093, t + 0.38, 0.15, 0.045, 'triangle');
      return;
    }

    if (result === 'achievement') {
      playTone(graph, 784, t + 0.1, 0.12, 0.06, 'square');
      playTone(graph, 988, t + 0.23, 0.14, 0.06, 'square');
      playTone(graph, 1175, t + 0.38, 0.2, 0.055, 'triangle');
      playTone(graph, 1568, t + 0.38, 0.2, 0.04, 'triangle');
      return;
    }

    playGlide(graph, 205, 168, t + 0.12, 0.13, 0.095, 'sawtooth');
    playGlide(graph, 185, 152, t + 0.3, 0.13, 0.088, 'sawtooth');
    playGlide(graph, 165, 88, t + 0.48, 0.62, 0.1, 'sawtooth');
    playTone(graph, 75, t + 1.08, 0.24, 0.065, 'triangle');
  } catch {
    // ignore
  }
}

/**
 * Expose the game sounds to the global object for debugging purposes.
 */
if (process.env.NODE_ENV === 'development') {
  (globalThis as unknown as Record<string, unknown>).gameSounds = {
    playGameStartSound,
    playOpponentFoundSound,
    playMoveSound,
    playHitSound,
    playShipSunkSound,
    playWinSound,
    playLoseSound,
    playChestOpenSound,
  };
}
